import { useCallback, type MouseEvent } from 'react';
import { Button, Link, type LinkProps } from '@geberit/gdds';

// types
import type { ContentProps } from './ContentProps';

// utils
import { mailLink } from './trackingActions';
import { useTracking } from 'utils/hooks/useTracking';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useGdds } from 'utils/hooks/use-gdds';
import { useNord } from 'utils/hooks/use-nord';
import { isEmpty } from 'utils/is-empty';
import { useTracking as useGA4Tracking } from 'utils/tracking/track';

function renderLinkContent(children: React.ReactNode, content: ContentProps) {
  return children ?? content.text;
}

export type MailLinkProps = {
  content: ContentProps;
  className?: string;
  tracking?(e: MouseEvent<HTMLAnchorElement>): void;
  socialTrackingName?: string;
  socialTrackingIcon?: string;
  trackingCategory?: string;
  stylingType?: LinkProps['stylingType'];
  alignByContent?: LinkProps['alignByContent'];
  standardFontSize?: boolean;
  inContext?: boolean;
  icon?: string;
};

export function MailLink({
  content,
  children,
  className = '',
  tracking,
  socialTrackingName,
  socialTrackingIcon,
  trackingCategory,
  stylingType,
  alignByContent,
  standardFontSize = true,
  inContext,
  icon,
}: Readonly<React.PropsWithChildren<MailLinkProps>>) {
  const isGdds = useGdds();
  const isNord = useNord();
  const track = useTracking();
  const { trackEvent } = useGA4Tracking();

  const { target, type, showArrow, buttonStyle, isIcon } = content;

  const isButton = type === 'mail_button';

  const handleTracking = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (!tracking) {
      let link = (e.target as HTMLAnchorElement).href;
      if (socialTrackingIcon) {
        link = ((e.target as HTMLAnchorElement).parentNode as HTMLAnchorElement)?.href;
      }

      if (!link) {
        link = content.target;
      }

      const genericTrackingLink = String(link).includes('@') ? link.replace('@', '__') : link;
      track.trackEvent(mailLink(
        genericTrackingLink,
        socialTrackingName,
        socialTrackingIcon,
        trackingCategory,
      ));

      trackEvent({
        event: 'click',
        click_intent: 'contact',
        click_element: 'email',
        click_text: link.replace(/^mailto:/, ''),
      });
    } else {
      tracking(e);
    }
  }, []);

  if (isGdds) {
    const defaultStylingType: 'secondary' | 'primary' = content.showArrow ? 'secondary' : 'primary';
    const alignment = alignByContent ?? (content.showArrow || inContext ? undefined : 'left');

    const LinkComponent = isButton ? Button : Link;
    const buttonContent = isButton && isEmpty(children) ? content.text : children;

    return (
      <LinkComponent
        stylingType={buttonStyle ?? stylingType ?? defaultStylingType}
        text={renderLinkContent(children, content)}
        target={target}
        onClick={handleTracking}
        hasArrow={showArrow ?? false}
        alignByContent={alignment}
        className={classNameBuilder(className, isNord && 'isNordicsStyle')}
        standardFontSize={standardFontSize}
        inContext={inContext}
        icon={icon}
        inheritFontSize
        {...(isButton && ( { as: 'a', href: target, isIcon }))}
      >
        {isButton && (buttonContent as any)}
      </LinkComponent>
    );
  }

  const ariaLabel = content.text || content.target || undefined;

  return (
    <a href={content.target} className={className} onClick={handleTracking} aria-label={ariaLabel}>
      {renderLinkContent(children, content)}
    </a>
  );
}
